import type { GoNoGoResult } from '@/lib/schema'
import { formatPercent, formatCurrency } from '@/lib/formulas'
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { clsx } from 'clsx'

interface GoNoGoVerdictProps {
  result: GoNoGoResult
  className?: string
}

export function GoNoGoVerdict({ result, className }: GoNoGoVerdictProps) {
  const { verdict, checks, passedCount, totalChecks } = result

  const verdictConfig = {
    pass: {
      icon: CheckCircle,
      label: 'PASS',
      bgColor: 'bg-success-50',
      borderColor: 'border-success-200',
      textColor: 'text-success-700',
      iconColor: 'text-success-500',
    },
    fail: {
      icon: XCircle,
      label: 'FAIL',
      bgColor: 'bg-danger-50',
      borderColor: 'border-danger-200',
      textColor: 'text-danger-700',
      iconColor: 'text-danger-500',
    },
    warning: {
      icon: AlertCircle,
      label: 'WARNING',
      bgColor: 'bg-warning-50',
      borderColor: 'border-warning-200',
      textColor: 'text-warning-700',
      iconColor: 'text-warning-500',
    },
  }

  const config = verdictConfig[verdict]
  const Icon = config.icon

  const formatValue = (criterion: string, value: number): string => {
    if (criterion === 'Monthly Cash Flow') {
      return formatCurrency(value)
    }
    if (criterion === 'GRM' || criterion === 'DSCR') {
      return value.toFixed(2)
    }
    return formatPercent(value)
  }

  return (
    <div className={clsx('rounded-xl border p-5', config.bgColor, config.borderColor, className)}>
      <div className="flex items-center gap-3">
        <Icon className={clsx('h-8 w-8', config.iconColor)} />
        <div>
          <h3 className={clsx('text-xl font-bold', config.textColor)}>{config.label}</h3>
          <p className="text-sm text-slate-600">
            {passedCount}/{totalChecks} criteria passed
          </p>
        </div>
      </div>

      {checks.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="pb-2 text-left font-medium text-slate-600">Criterion</th>
                <th className="pb-2 text-right font-medium text-slate-600">Threshold</th>
                <th className="pb-2 text-right font-medium text-slate-600">Actual</th>
                <th className="pb-2 text-center font-medium text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {checks.map((check) => (
                <tr key={check.criterion} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 text-slate-900">
                    {check.criterion}
                    <span className="ml-1 text-xs text-slate-400">
                      ({check.comparisonType === 'min' ? 'min' : 'max'})
                    </span>
                  </td>
                  <td className="py-2 text-right font-mono text-slate-600">
                    {formatValue(check.criterion, check.threshold)}
                  </td>
                  <td
                    className={clsx(
                      'py-2 text-right font-mono font-medium',
                      check.passed ? 'text-success-600' : 'text-danger-600'
                    )}
                  >
                    {formatValue(check.criterion, check.actual)}
                  </td>
                  <td className="py-2 text-center">
                    {check.passed ? (
                      <CheckCircle className="inline h-5 w-5 text-success-500" />
                    ) : (
                      <XCircle className="inline h-5 w-5 text-danger-500" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
